import React, { useEffect, useMemo, useRef, useState } from 'react';
import bemCn from 'bem-cn';
import loadable from '@loadable/component';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import PictureSet from '../picture-set';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import './carousel-inhouse.scss';

const RequestMorePhotos = loadable(() => import('../request-more-photos/index-ssr'));

const b = bemCn('carousel-inhouse');

const SPOT_MIN_REQ_PHOTOS = 10;

const ArrowBtn = ({ index, length, next, prev }) => {
    if (length < 1) {
        return '';
    }

    const maxIndex = length < SPOT_MIN_REQ_PHOTOS ? length : length - 1;

    return (
        <>
            <button className={b('arrows prev')} style={{ visibility: index == 0 ? 'hidden' : 'visible' }} onClick={prev}>
                <SvgChevronRight />
            </button>
            <button className={b('arrows next')} style={{ visibility: index == maxIndex ? 'hidden' : 'visible' }} onClick={next}>
                <SvgChevronRight />
            </button>
        </>
    );
};

const EmptyPublic = () => (
    <div className="request-more-photos">
        <p className="snif-p snif-semibold no-height text-center">No available photo</p>
    </div>
);

const ReqPhotos = ({ spotId, publicPark }) => (
    <div className="car-ssr-req-container">
        <RequestMorePhotos id={spotId} publicPark={publicPark} />
    </div>
);

const CarouselInhouse = ({ spotId, myPhotos, method, spotGallery, imageAlt, publicPark, photosCount, setPhotosIdx }) => {
    const [index, setIndex] = useState(0);
    const [isFading, setIsFading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [touchStartX, setTouchStartX] = useState(null);
    const length = photosCount;
    const isShowReq = spotGallery && photosCount < SPOT_MIN_REQ_PHOTOS;

    const imgUrl = useMemo(() => {
        return ImageHelper.getUrlFromOptions(myPhotos[index]?.imageAws, `full-fit-in/${IMG_RESIZES.x540}`);
    }, [index, myPhotos]);

    const prevImgUrlRef = useRef(imgUrl);

    const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);

    const goTo = (newIdx) => {
        const newImgUrl = ImageHelper.getUrlFromOptions(myPhotos[newIdx]?.imageAws, `full-fit-in/${IMG_RESIZES.x540}`);

        if (newImgUrl === prevImgUrlRef.current) {
            setIndex(newIdx);
            return;
        }

        setIsFading(true);
        setIsLoading(true);
        setTimeout(() => {
            setIndex(newIdx);
            prevImgUrlRef.current = newImgUrl;
        }, 100);
    };

    const prev = (e) => {
        e?.preventDefault();
        if (index > 0) {
            goTo(index - 1);
        }
    };

    const next = (e) => {
        e?.preventDefault();
        const maxIndex = length < SPOT_MIN_REQ_PHOTOS ? length : length - 1;
        if (index < maxIndex) {
            goTo(index + 1);
        }
    };

    const handleTouchEnd = (e) => {
        if (touchStartX === null) {
            return;
        }
        const deltaX = touchStartX - e.changedTouches[0].clientX;
        if (deltaX > 50) {
            next();
        } // swipe left
        if (deltaX < -50) {
            prev();
        } // swipe right
        setTouchStartX(null);
    };

    useEffect(() => {
        setPhotosIdx && setPhotosIdx(index);

        if (index > 0 && !myPhotos[index]?.imageAws) {
            setIsFading(true);
            setIsLoading(true);

            const newImgUrl = ImageHelper.getUrlFromOptions(myPhotos[index]?.imageAws, `full-fit-in/${IMG_RESIZES.x540}`);

            if (newImgUrl === prevImgUrlRef.current) {
                setIsFading(false);
                setIsLoading(false);
            }
        }
    }, [index]);

    return (
        <div className={b()}>
            <div
                className={`carousel-image ${isFading ? 'fade-out' : isLoading ? '' : 'fade-in'}`}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {isShowReq && (length == 0 || index == length) ? (
                    publicPark ? (
                        <EmptyPublic />
                    ) : (
                        <ReqPhotos {...{ spotId }} />
                    )
                ) : (
                    <PictureSet
                        imgs={imgUrl}
                        alt={`${imageAlt} ${index + 1}`}
                        method={index == 0 ? method : 'lazy'}
                        fetchpriority={method == 'eager' ? 'high' : 'auto'}
                        onLoad={() => {
                            setIsLoading(false);
                            setIsFading(false);
                        }}
                        force
                    />
                )}
                {length > 0 && (
                    <div className="spot-detail-photos-counter">
                        <div className="photos-text">
                            {index + 1 > length ? length : index + 1} of {length}
                        </div>
                    </div>
                )}
            </div>
            <ArrowBtn {...{ index, length, next, prev }} />
        </div>
    );
};

export default CarouselInhouse;
